import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReportGenerationPanel = () => {
  const [selectedReportType, setSelectedReportType] = useState('comprehensive');
  const [selectedPeriod, setSelectedPeriod] = useState('q3-2024');
  const [selectedSections, setSelectedSections] = useState([
    'emissions', 'energy', 'waste', 'compliance'
  ]);
  const [isGenerating, setIsGenerating] = useState(false);

  const reportTypes = [
    { value: 'comprehensive', label: 'Comprehensive ESG Report' },
    { value: 'emissions', label: 'Carbon Emissions Report' },
    { value: 'energy', label: 'Energy Efficiency Report' },
    { value: 'compliance', label: 'Regulatory Compliance Report' },
    { value: 'sustainability', label: 'Sustainability Goals Report' },
    { value: 'executive', label: 'Executive Summary' }
  ];

  const reportPeriods = [
    { value: 'q1-2024', label: 'Q1 2024' },
    { value: 'q2-2024', label: 'Q2 2024' },
    { value: 'q3-2024', label: 'Q3 2024' },
    { value: 'q4-2024', label: 'Q4 2024' },
    { value: 'fy-2024', label: 'FY 2024' },
    { value: 'ytd-2024', label: 'YTD 2024' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const reportSections = [
    { id: 'emissions', label: 'Carbon Emissions', description: 'Scope 1, 2, and 3 emissions data', icon: 'TrendingDown' },
    { id: 'energy', label: 'Energy Consumption', description: 'Energy usage and efficiency metrics', icon: 'Zap' },
    { id: 'waste', label: 'Waste Management', description: 'Waste generation and diversion rates', icon: 'Recycle' },
    { id: 'water', label: 'Water Usage', description: 'Water consumption and conservation', icon: 'Droplets' },
    { id: 'compliance', label: 'Regulatory Compliance', description: 'Compliance status and audit results', icon: 'Shield' },
    { id: 'goals', label: 'Sustainability Goals', description: 'Progress toward environmental targets', icon: 'Target' },
    { id: 'supply-chain', label: 'Supply Chain', description: 'Supplier sustainability metrics', icon: 'Truck' },
    { id: 'biodiversity', label: 'Biodiversity', description: 'Environmental impact assessments', icon: 'Leaf' }
  ];

  const recentReports = [
    {
      id: 1,
      name: 'Q2 2024 Comprehensive ESG Report',
      type: 'comprehensive',
      generated: '2024-08-01T10:30:00Z',
      size: '2.4 MB',
      format: 'PDF',
      status: 'completed'
    },
    {
      id: 2,
      name: 'July 2024 Carbon Emissions Report',
      type: 'emissions',
      generated: '2024-08-05T14:15:00Z',
      size: '1.2 MB',
      format: 'PDF',
      status: 'completed'
    },
    {
      id: 3,
      name: 'Energy Efficiency Analysis - YTD',
      type: 'energy',
      generated: '2024-08-10T09:45:00Z',
      size: '1.8 MB',
      format: 'Excel',
      status: 'completed'
    }
  ];

  const handleSectionToggle = (sectionId) => {
    setSelectedSections(prev => 
      prev?.includes(sectionId)
        ? prev?.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    setIsGenerating(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
          <Icon name="FileText" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Automated Report Generation</h3>
          <p className="text-sm text-muted-foreground">Generate comprehensive sustainability reports</p>
        </div>
      </div>
      {/* Report Configuration */}
      <div className="space-y-6 mb-8">
        {/* Report Type */}
        <div>
          <Select
            label="Report Type"
            description="Choose the type of sustainability report to generate"
            options={reportTypes}
            value={selectedReportType}
            onChange={setSelectedReportType}
          />
        </div>

        {/* Report Period */}
        <div>
          <Select
            label="Reporting Period"
            description="Select the time period for the report"
            options={reportPeriods}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
          />
        </div>

        {/* Report Sections */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Report Sections
          </label>
          <p className="text-sm text-muted-foreground mb-4">
            Select the sections to include in your report
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reportSections?.map((section) => (
              <div key={section?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                <Checkbox
                  checked={selectedSections?.includes(section?.id)}
                  onChange={() => handleSectionToggle(section?.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name={section?.icon} size={16} color="var(--color-primary)" />
                    <span className="font-medium text-foreground">{section?.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{section?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Generate Button */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {selectedSections?.length} sections selected
          </div>
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            loading={isGenerating}
            onClick={handleGenerateReport}
            disabled={selectedSections?.length === 0}
          >
            {isGenerating ? 'Generating Report...' : 'Generate Report'}
          </Button>
        </div>
      </div>
      {/* Recent Reports */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-foreground">Recent Reports</h4>
          <Button variant="ghost" iconName="MoreHorizontal" className="text-xs">
            View All
          </Button>
        </div>

        <div className="space-y-3">
          {recentReports?.map((report) => (
            <div key={report?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                  <Icon name="FileText" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{report?.name}</p>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <span>{formatDate(report?.generated)}</span>
                    <span>•</span>
                    <span>{report?.size}</span>
                    <span>•</span>
                    <span>{report?.format}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" iconName="Download" className="text-xs">
                  Download
                </Button>
                <Button variant="ghost" iconName="Share" className="text-xs">
                  Share
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="border-t border-border pt-6 mt-6">
        <h4 className="font-medium text-foreground mb-4">Quick Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button variant="outline" iconName="Calendar" iconPosition="left" className="justify-start">
            Schedule Automated Reports
          </Button>
          <Button variant="outline" iconName="Mail" iconPosition="left" className="justify-start">
            Email Distribution List
          </Button>
          <Button variant="outline" iconName="Settings" iconPosition="left" className="justify-start">
            Report Templates
          </Button>
          <Button variant="outline" iconName="Archive" iconPosition="left" className="justify-start">
            Report Archive
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerationPanel;